import moment from 'moment'
import { isNull } from 'lodash'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { useState, useEffect } from 'react'
import CreateIcon from '@material-ui/icons/Create'

import { Colors } from '~/styles'
import { CodeCell } from '~/components'
import { FormFilledIcon } from '~/components/svg'
import { VesselForecastAPI } from '~/api'
import { GenerateFilterTerm, ConvertLocalTimeGet } from '~/utils'

import Styled from './vessel-forecast.styled'
import Helper from './vessel-forecast.helper'
import GridColumn from './vessel-forecast.column.json'
import { SET_USER_PROFILE_LOCATION } from '../../redux/actions'

const useHooks = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const appSize = useSelector((state) => state.appReducer)
  const { Location } = useSelector((state) => state.usersProfileReducer)
  const { roleMapping } = useSelector((state) => state.usersLoginReducer)
  const { UserLocations } = useSelector((state) => state.usersLoginReducer)
  const { publicRuntimeConfig } = getConfig()

  const [, setFirstLoad] = useState(true)
  const [alert, setAlert] = useState(Helper.DefaultAlert)
  const [dataItem, setDataItem] = useState()
  const [pending, setPending] = useState(false)
  const [dataGrid, setDataGrid] = useState([])
  const [dataCount, setDataCount] = useState(0)
  const [gridColumnActive] = useState(GridColumn)
  const [actionType, setActionType] = useState('view')
  const [openManageModal, setOpenManageModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openCreateSRModal, setOpenCreateSRModal] = useState(false)
  const [gridColumnLoading, setGridColumnLoading] = useState(true)
  const [filterCounts, setFilterCounts] = useState(Helper.FilterCounts)
  const [dataState, setDataState] = useState(Helper.DefaultDataState)
  const [selectedFilter, setSelectedFilter] = useState()

  const pathRole = roleMapping.find((e) => e.path === router.pathname)
  const toggleManageModal = () => setOpenManageModal((prev) => !prev)
  const toggleDeleteModal = () => setOpenDeleteModal((prev) => !prev)
  const toggleCreateSRModal = () => setOpenCreateSRModal((prev) => !prev)

  const handleToggleAlert = ({ msg, success, visible, type }) => {
    setAlert({ msg, success, visible, type })
  }

  const onDataStateChange = ({ data }) => {
    GenerateFilterTerm(data.filter, GridColumn)
    setDataState({
      ...data,
      filter: data.filter,
      filterString: GenerateFilterTerm(data.filter, GridColumn)
    })
  }

  const onPageChange = (event) => {
    setDataState({
      ...dataState,
      skip: event.page.skip,
      take: event.page.take
    })

    fetchData({
      skip: event.page.skip,
      take: event.page.take,
      status: selectedFilter?.value,
    })
  }
  const heightGrid = appSize.height - 240 > 455 ? appSize.height - 240 : 455

  const legendTable = [{ label: 'Overlapping Schedule / Schedule Conflict', color: Colors.lightPink }]

  const formatDate = (location, date, format) => {
    const formattedDate = moment(date).format(format)
    return formattedDate === 'Invalid date' ? '' : ConvertLocalTimeGet(location, date, format)
  }

  const mapResultData = (data) => {
    return data.map((e) => {
      e['all_fast_non_formatted'] = e['all_fast']
      e['cast_off_non_formatted'] = e['cast_off']
      e['all_fast'] = formatDate(Location.LocationDescription, e.all_fast, 'DD/MM/YYYY HH:mm')
      e['cast_off'] = formatDate(Location.LocationDescription, e.cast_off, 'DD/MM/YYYY HH:mm')
      e['laycan_end_formatted'] = formatDate(Location.LocationDescription, e.laycan_end, 'DD/MM/YYYY')
      e['laycan_start_formatted'] = formatDate(Location.LocationDescription, e.laycan_start, 'DD/MM/YYYY')
      return e
    })
  }

  const mapResultDataCounts = (data) => {
    setFilterCounts((prev) => {
      const updatedCounts = prev.map((item) => {
        const key = item.key
        if (data[key] !== undefined) {
          return {
            ...item,
            count: data[key], // Update the count based on the matching key in data
          }
        }
        return item
      })
      return updatedCounts
    })
  }

  const deleteVesselForecast = async () => {
    try {
      const response = await VesselForecastAPI.DeleteVesselForecast(dataItem.id)
      if (response.data.result) {
        setAlert({
          msg: 'Delete data has been successful',
          success: true,
          visible: true,
          type: 'delete',
        })
      } else {
        setAlert({ msg: 'Cannot delete data', success: false, visible: true, type: 'delete' })
      }
    } catch (err) {
      return err
    } finally {
      toggleDeleteModal()
      setDataItem(undefined)
      fetchData({ ...dataState, status: selectedFilter?.value })
    }
  }

  const fetchData = async ({ skip, take, status }) => {
    try {
      setPending(true)
      const generateFilter = GenerateFilterTerm(dataState?.filter, GridColumn)

      // Parse the filter string and merge with location_code
      const parsedFilter = JSON.parse(generateFilter)
      const filterParsedFilter = parsedFilter.length > 0 && parsedFilter.filter((e) => e.field !== 'location_code')

      // Merge the parsed filter with the location_code filter
      // If Location is defined and has a LocationID, add it to the filter
      const mergedFilter = Location && Location.LocationID && filterParsedFilter.length > 0
        ? [
          ...filterParsedFilter,
            { type: 'text', field: 'location_code', value: Location.LocationCode }
          ]
        : [{ type: 'text', field: 'location_code', value: Location.LocationCode }]

      // Prepare the payload for the API call
      const payload = {
        skip,
        take,
        status,
        filters: JSON.stringify(mergedFilter),
      }

      const result = await VesselForecastAPI.GetAllDataVesselForecast(payload)

      if (!result.error) {
        setGridColumnLoading(false)
        setDataGrid(isNull(result.data) ? [] : mapResultData(result.data))
        setDataCount(selectedFilter ? result.dataCount?.[selectedFilter.key] : result.dataCount.total_data)
        mapResultDataCounts(result.dataCount)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setPending(false)
      setGridColumnLoading(false)
    }
  }

  const _onClose = () => {
    setAlert(false)
  }

  const handleFilterClick = (item) => {
    setSelectedFilter((prevSelected) => (prevSelected?.value === item.value ? undefined : item))
  }

  const handleClickAction = (dataItem, action, hasAccess = true) => {
    if (!hasAccess) return
    if (action !== 'create') {
      setDataItem(dataItem)
    }
    if (action === 'create') {
      setActionType('create')
      toggleManageModal()
    }
    if (action === 'edit') {
      setActionType('edit')
      toggleManageModal()
    }
    if (action === 'view') {
      setActionType('view')
      toggleManageModal()
    }
    if (action === 'delete') {
      toggleDeleteModal()
    }
    if (action === 'create-sr') {
      setDataItem(dataItem)
      toggleCreateSRModal()
    }
  }

  useEffect(() => {
    const applyFilterWithDelay = () => {
      fetchData({ ...dataState, status: selectedFilter?.value })
      setFirstLoad(false)
    }

    const delay = setTimeout(applyFilterWithDelay, publicRuntimeConfig.FILTER_TIMEOUT)

    return () => clearTimeout(delay)
  }, [dataState.filter, selectedFilter?.value, Location?.LocationCode])

  const _statusCell = ({ colSpan, dataItem }) => {
    const status = String(dataItem.plan_name).toLowerCase()

    const statusColors = {
      closed: { background: Colors.white, color: Colors.darkSlate },
      default: { background: Colors.deepBlue, color: Colors.white },
      unresolved: { background: Colors.vividOrange, color: Colors.white },
      'vessel berthing': { background: Colors.lightOlive, color: Colors.cancelTextColor },
      'upcoming vessel': { background: Colors.lemonYellow, color: Colors.cancelTextColor },
    }

    const { background, color } = statusColors[status] || statusColors.default

    if (dataItem.plan_name) {
      return (
        <td>
          <Styled.ActionContainer id={`status_${dataItem.plan_name}`} colSpan={colSpan}>
            <Styled.StatusCell backgroundColor={background} color={color}>
              {dataItem.plan_name}
            </Styled.StatusCell>
          </Styled.ActionContainer>
        </td>
      )
    } else {
      return <td></td>
    }
  }

  const _actionCell = ({ colSpan, dataItem }) => {
    const actionByStatus = Helper.EnableDisableAction(dataItem)
    const isEditEnabled = pathRole.allowEdit && actionByStatus.edit
    const isGenerateEnabled = pathRole.allowCreate && actionByStatus.generate

    return (
      <td
        style={{ position: 'sticky', left: '0px', zIndex: 10 }}
        className='k-grid-content-sticky'
        id='m_clnt_action_btn'
        colSpan={colSpan}
      >
        <Styled.ActionContainer>
           <div
            id={`m-vessel-forecast-delete-row-${dataItem.id}-btn`}
            onClick={() => handleClickAction(dataItem, 'create-sr', isGenerateEnabled)}
            style={{ cursor: isGenerateEnabled ? 'pointer' : 'default' }}
          >
            <FormFilledIcon color={isGenerateEnabled ? Colors.btnBlue : Colors.grey} />
          </div>
          <div
            id={`m-vessel-forecast-edit-row-${dataItem.id}-btn`}
            onClick={() => handleClickAction(dataItem, 'edit', isEditEnabled)}
            style={{ cursor: isEditEnabled ? 'pointer' : 'default' }}
          >
            <CreateIcon htmlColor={isEditEnabled ? Colors.green : Colors.grey} />
          </div>
        </Styled.ActionContainer>
      </td>
    )
  }

  const _codeCell = ({ dataItem }) => {
    return (
      <CodeCell
        lock
        style={{ cursor: pathRole.allowView ? 'pointer' : 'default' }}
        disableTooltip={!pathRole.allowView}
        id={`vessel_name_${dataItem.vessel_name}`}
        onClick={() => handleClickAction(dataItem, 'view')}
      >
        {dataItem.vessel_name}
      </CodeCell>
    )
  }

  const _overlapCell = (props) => {
    const status = String(props.dataItem.plan_name).toLowerCase()
    const customStyle = {
      color: Colors.darkSlate,
      borderBottom: '1px solid rgb(0, 0, 0, 0.08)',
      backgroundColor: Colors.lightPink,
    }

    return (
      <td
        id={`m_grid_cell_all_fast_${props.dataItem.id}`}
        style={props.dataItem.is_overlap && status !== 'closed' ? customStyle : {}}
      >
        {props.dataItem[props.field]}
      </td>
    )
  }

  const _setLocation = (value) => {
    const valueLocation = UserLocations.find((e) => e.LocationDescription === value)

    dispatch({
      type: SET_USER_PROFILE_LOCATION,
      payload: valueLocation,
    })
  }

  return {
    data: {
      alert,
      pending,
      dataItem,
      dataGrid,
      pathRole,
      dataState,
      dataCount,
      heightGrid,
      actionType,
      legendTable,
      filterCounts,
      selectedFilter,
      openManageModal,
      openDeleteModal,
      gridColumnActive,
      gridColumnLoading,
      openCreateSRModal,
    },
    methods: {
      _onClose,
      _codeCell,
      _statusCell,
      _actionCell,
      _overlapCell,
      _setLocation,
      fetchData,
      onPageChange,
      handleToggleAlert,
      onDataStateChange,
      toggleManageModal,
      toggleDeleteModal,
      handleClickAction,
      handleFilterClick,
      toggleCreateSRModal,
      deleteVesselForecast,
    }
  }
}

export default useHooks